import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkoutTile from '../../../../component/HealthComponent/workoutTile';
import { getExercises, deleteExercise } from '../../../../api/healthAPI';
import { errorToast, successToast, getLocalStorageItem } from '../../../../utils/helper';
import ConfirmPopup from '../../../../component/common/modals/ConfirmPopup';

const FitnessExercises = ({ searchKey }) => {
  const navigate = useNavigate();
  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchExercises();
  }, [searchKey]);

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const fetchExercises = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        limit: 6,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        ...(searchKey && { search: searchKey })
      };
      const response = await getExercises(params);
      if (response?.meta?.code === 1 && response?.data) {
        setExercises(response.data);
      } else {
        setExercises([]);
      }
    } catch (error) {
      setExercises([]);
      errorToast('Failed to fetch exercises');
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseClick = (exercise) => {
    navigate(
      `${userData?.slug ? `/${userData.slug}` : ''}/health/exercise/new?id=${exercise._id}`,
      { state: { _id: exercise._id, action: 'edit' } }
    );
  };

  const handleMenuClick = (e, exercise) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === exercise._id ? null : exercise._id);
  };

  const handleEdit = (exercise) => {
    navigate(
      `${userData?.slug ? `/${userData.slug}` : ''}/health/exercise/new?id=${exercise._id}`,
      { state: { _id: exercise._id, action: 'edit' } }
    );
    setOpenMenuId(null);
  };

  const handleDelete = (exercise) => {
    setExerciseToDelete(exercise);
    setShowDeleteConfirm(true);
    setOpenMenuId(null);
  };

  const confirmDelete = async () => {
    if (!exerciseToDelete?._id) return;

    try {
      setIsDeleting(true);
      const response = await deleteExercise(exerciseToDelete._id);
      if (response?.meta?.code === 1) {
        successToast(response?.meta?.message || 'Exercise deleted successfully');
        fetchExercises();
      } else {
        errorToast(response?.meta?.message || 'Failed to delete exercise');
      }
    } catch (error) {
      errorToast('Failed to delete exercise');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setExerciseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setExerciseToDelete(null);
  };

  const handleViewAll = () => {
    navigate(`${userData?.slug ? `/${userData.slug}` : ''}/health/exercises`);
  };

  return (
    <div className="bg-white mt-4 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Exercises</h2>
        <button
          onClick={handleViewAll}
          className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-1 transition-colors"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {!loading && exercises.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {exercises.map((exercise) => (
            <div key={exercise._id} className="relative">
              <WorkoutTile
                image={exercise.image_url}
                title={exercise.name}
                usageCount={exercise.usage_count || 0}
                badgeText={exercise.muscle_groups?.[0] || exercise.difficulty || 'Exercise'}
                onClick={() => handleExerciseClick(exercise)}
                onMenuClick={(e) => handleMenuClick(e, exercise)}
              />

              {openMenuId === exercise._id && (
                <div className="absolute top-12 right-2 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px]">
                  <button
                    onClick={() => handleEdit(exercise)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(exercise)}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && exercises.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No exercises</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new exercise.</p>
        </div>
      )}

      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={confirmDelete}
        handleNo={handleDeleteCancel}
        message={`Are you sure you want to delete "${exerciseToDelete?.name}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default FitnessExercises;
